<template>
    <button
        type="button"
        class="inline-flex h-full min-h-12 w-12 items-center justify-center rounded-xl border border-foxy-border bg-foxy-elevated text-foxy-action transition-colors hover:border-foxy-action hover:bg-foxy-action/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foxy-action focus-visible:ring-offset-2 focus-visible:ring-offset-foxy-background disabled:cursor-wait disabled:opacity-60"
        :aria-label="actionLabel"
        :title="actionLabel"
        :aria-pressed="isFavorite ? 'true' : 'false'"
        :aria-busy="pending ? 'true' : 'false'"
        :disabled="pending || (!initialState.can_mutate && !loginUrl)"
        @click="toggle"
    >
        <svg
            class="h-7 w-7"
            viewBox="0 0 32 32"
            aria-hidden="true"
            focusable="false"
            :fill="isFavorite ? 'currentColor' : 'none'"
            stroke="currentColor"
            :stroke-width="isFavorite ? 0.35 : 1.9"
            stroke-linecap="round"
            stroke-linejoin="round"
        >
            <!-- Final Pawtail Emblem: A's brush silhouette with balanced paw pads. -->
            <!-- Filled contours are slightly inset to balance their optical weight. -->
            <path :d="isFavorite ? 'M7.64 12.12C7.24 13.99 5.83 15.27 4.48 14.98C3.13 14.69 2.36 12.95 2.76 11.08C3.16 9.21 4.57 7.93 5.92 8.22C7.27 8.51 8.04 10.25 7.64 12.12ZM15.34 6.14C14.93 8.06 13.46 9.37 12.06 9.07C10.66 8.78 9.86 6.98 10.26 5.06C10.67 3.14 12.14 1.83 13.54 2.13C14.94 2.42 15.74 4.22 15.34 6.14ZM22.84 6.82C22.43 8.74 21.01 10.06 19.66 9.77C18.31 9.49 17.55 7.70 17.96 5.78C18.37 3.86 19.79 2.54 21.14 2.83C22.49 3.11 23.25 4.90 22.84 6.82ZM7.3 27.9C4.4 23.8 5.2 19.1 9.4 16.5C13.2 14.1 17.2 15.3 21 12.7C24 10.9 26 8.5 26.8 5.8C30.4 13 29.8 20.6 24.6 24.5C19.1 28 11.7 24.9 7.3 27.9Z' : 'M7.74 12.14C7.33 14.09 5.86 15.42 4.45 15.12C3.05 14.82 2.24 13.00 2.66 11.06C3.07 9.11 4.54 7.78 5.95 8.08C7.35 8.38 8.16 10.20 7.74 12.14ZM15.44 6.16C15.02 8.16 13.49 9.53 12.03 9.22C10.57 8.91 9.73 7.04 10.16 5.04C10.58 3.04 12.11 1.67 13.57 1.98C15.03 2.29 15.87 4.16 15.44 6.16ZM22.94 6.84C22.52 8.84 21.04 10.22 19.63 9.92C18.23 9.62 17.43 7.76 17.86 5.76C18.28 3.76 19.76 2.38 21.17 2.68C22.57 2.98 23.37 4.84 22.94 6.84ZM6.8 28.2C3.7 23.8 4.8 18.6 9 16C13.2 13.4 17 15.1 21 12.7C24 10.9 26 8.5 26.8 5.8C30.4 13 29.8 20.6 24.6 24.5C19 28.6 11.4 24.8 6.8 28.2Z'" />
            <path
                :d="isFavorite ? 'M21 12.7C24 10.9 26 8.5 26.8 5.8C30.4 13 29.8 20.6 24.6 24.5C24.215 24.745 23.821 24.958 23.419 25.142C24.6 23.8 24.9 21.9 24.5 19.7L23 20.6C24 16.4 22.7 13.7 21 12.7Z' : 'M21 12.7C22.7 13.7 24 16.4 23 20.6L24.5 19.7C24.9 21.9 24.6 23.8 23.396 25.250'"
                stroke="currentColor"
                :stroke-width="isFavorite ? 0.9 : 1.9"
                :fill="isFavorite ? 'rgb(var(--foxy-elevated))' : 'none'"
            />
        </svg>
    </button>
</template>

<script>
import axios from 'axios';

export default {
    name: 'FavoriteToggle',
    props: {
        initialState: { type: Object, required: true },
        addUrl: { type: String, required: true },
        removeUrl: { type: String, required: true },
        loginUrl: { type: String, default: null },
    },
    data() {
        return {
            isFavorite: this.initialState.is_favorite,
            favoritedAt: this.initialState.favorited_at,
            pending: false,
        };
    },
    computed: {
        actionLabel() {
            return this.isFavorite ? 'Прибрати з улюбленого' : 'Додати до улюбленого';
        },
    },
    methods: {
        async toggle() {
            if (this.pending) return;
            if (!this.initialState.can_mutate) {
                if (this.loginUrl) window.location.href = this.loginUrl;
                return;
            }

            this.pending = true;
            try {
                const { data } = this.isFavorite
                    ? await axios.delete(this.removeUrl)
                    : await axios.put(this.addUrl);
                if (
                    typeof data?.is_favorite !== 'boolean'
                    || (data.is_favorite
                        ? typeof data.favorited_at !== 'string' || !data.favorited_at
                        : data.favorited_at !== null)
                ) {
                    throw new Error('Invalid favorite state');
                }
                this.isFavorite = data.is_favorite;
                this.favoritedAt = data.favorited_at;
            } catch (_) {
                this.$emit('notification', {
                    type: 'error',
                    message: 'Не вдалося змінити улюблене. Спробуйте ще раз.',
                });
            } finally {
                this.pending = false;
            }
        },
    },
};
</script>
